function isTextQuestionAnswered(value) {
  return typeof value === "string" ? value.trim().length > 0 : value != null;
}

function isSingleChoiceAnswered(value) {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed);
  }

  return false;
}

function isMultiChoiceAnswered(value) {
  return Array.isArray(value) && value.length > 0;
}

function hasValidAnswer(question, value) {
  if (!question) {
    return true;
  }

  if (question.questionType === "MULTIPLE_CHOICE") {
    if (question.allowsMultiple) {
      return isMultiChoiceAnswered(value);
    }
    return isSingleChoiceAnswered(value);
  }

  return isTextQuestionAnswered(value);
}

function getTriggeredFollowUps(question, value) {
  const options = Array.isArray(question?.options) ? question.options : [];

  if (question?.questionType !== "MULTIPLE_CHOICE" || options.length === 0) {
    return [];
  }

  if (question.allowsMultiple) {
    const selected = Array.isArray(value)
      ? value.map((v) => Number(v)).filter((v) => Number.isFinite(v))
      : [];

    return options
      .filter((opt) => selected.includes(Number(opt.optionId)) && opt.followUpQuestion)
      .map((opt) => opt.followUpQuestion);
  }

  const selected = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(selected)) {
    return [];
  }

  const selectedOption = options.find((opt) => Number(opt.optionId) === selected);
  return selectedOption?.followUpQuestion ? [selectedOption.followUpQuestion] : [];
}

function validateQuestionTree(question, answers, missingByQuestionId) {
  if (!question || question.questionId == null) {
    return;
  }

  const value = answers?.[question.questionId];

  if (question.required && !hasValidAnswer(question, value)) {
    if (!missingByQuestionId.has(question.questionId)) {
      missingByQuestionId.set(question.questionId, question.questionText || `Fråga ${question.questionId}`);
    }
  }

  const triggeredFollowUps = getTriggeredFollowUps(question, value);
  triggeredFollowUps.forEach((followUp) =>
    validateQuestionTree(followUp, answers, missingByQuestionId)
  );
}

function validateSections(sections, answers, missingByQuestionId) {
  (sections || []).forEach((section) => {
    (section?.questions || []).forEach((question) =>
      validateQuestionTree(question, answers, missingByQuestionId)
    );

    validateSections(section?.children || [], answers, missingByQuestionId);
  });
}

export function validateRequiredQuestions(sections = [], answers = {}) {
  const missingByQuestionId = new Map();
  validateSections(sections, answers, missingByQuestionId);

  return {
    isValid: missingByQuestionId.size === 0,
    missingQuestions: Array.from(missingByQuestionId.values()),
    missingQuestionIds: Array.from(missingByQuestionId.keys()),
  };
}

function countQuestions(section) {
  const own = (section?.questions || []).length;
  const nested = (section?.children || []).reduce((sum, child) => sum + countQuestions(child), 0);
  return own + nested;
}

/**
 * Per-section completeness summary used by the section navigator.
 * Counts unanswered required questions (including triggered follow-ups and
 * nested child sections) for every top-level section.
 *
 * @returns {Array<{sectionId, code, title, label, questionCount, missing, complete}>}
 */
export function getSectionStatuses(sections = [], answers = {}) {
  return (sections || []).map((section) => {
    const missingByQuestionId = new Map();
    validateSections([section], answers, missingByQuestionId);

    const code = section?.sectionCode || "";
    const title = section?.sectionTitle || "";

    return {
      sectionId: section?.sectionId,
      code,
      title,
      label: [code, title].filter(Boolean).join(" "),
      questionCount: countQuestions(section),
      missing: missingByQuestionId.size,
      complete: missingByQuestionId.size === 0,
    };
  });
}
